import React, { useState } from 'react';
import { Box, Typography, Chip, Stack, LinearProgress, Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip, TextField, Button, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const REFRAME_TYPE_LABELS = {
  problem_to_opportunity: 'Problem → Opportunity',
  constraint_to_enabler: 'Constraint → Enabler',
  solution_to_outcome: 'Solution → Outcome',
  feature_to_capability: 'Feature → Capability',
  individual_to_collective: 'Individual → Collective',
  short_to_long_term: 'Short-term → Long-term',
  risk_to_readiness: 'Risk → Readiness',
  activity_to_metric: 'Activity → Metric',
  how_might_we: 'How Might We',
  job_story: 'Job Story'
};

const REFRAME_TYPE_COLORS = {
  problem_to_opportunity: '#10b981',
  constraint_to_enabler: '#3b82f6',
  solution_to_outcome: '#8b5cf6',
  feature_to_capability: '#ec4899',
  individual_to_collective: '#f59e0b',
  short_to_long_term: '#14b8a6',
  risk_to_readiness: '#ef4444',
  activity_to_metric: '#6366f1',
  how_might_we: '#a855f7',
  job_story: '#06b6d4'
};

export default function ReframesDisplay({ data, onRegenerateItem, onEdit, userEdited, onGenerateConcepts, onGenerateWording, generatingConcepts, generatingWording, isOrientation = false }) {
  const reframes = data?.reframes || [];
  const coverage = data?.coverage || {};
  const unknowns = data?.unknowns || [];
  const assumptions = data?.assumptions || [];
  const confidence = data?.confidence || 0;
  const generationStage = data?.generation_stage || 'complete'; // 'concepts', 'wording', 'complete'
  const [regeneratingIndex, setRegeneratingIndex] = useState(null);
  const [editingField, setEditingField] = useState(null); // Format: 'statement_0', 'why_0', 'next_move_0'
  const [editedValue, setEditedValue] = useState('');
  
  // Helper to check if reframe has wording
  const hasWording = (reframe) => {
    if (reframe.wording && reframe.wording.statement) return true;
    // Backward compatibility: check old format
    if (reframe.statement) return true;
    return false;
  };
  
  // Helper to get display values (support both new and old format)
  const getReframeDisplay = (reframe) => {
    if (reframe.wording) {
      return {
        statement: reframe.wording.statement,
        why: reframe.wording.why,
        next_move: reframe.wording.next_move
      };
    }
    // Backward compatibility
    return {
      statement: reframe.statement,
      why: reframe.why,
      next_move: reframe.next_move
    };
  };
  
  // Count reframes with and without wording
  const reframesWithWording = reframes.filter(rf => hasWording(rf)).length;
  const reframesWithoutWording = reframes.length - reframesWithWording;

  const handleRegenerateItem = async (index) => {
    if (typeof onRegenerateItem === 'function') {
      setRegeneratingIndex(index);
      try {
        await onRegenerateItem('reframe', index);
      } finally {
        setRegeneratingIndex(null);
      }
    }
  };
  
  const startEdit = (field, value) => {
    setEditingField(field);
    setEditedValue(value);
  };
  
  const saveEdit = (index, field, value) => {
    const newReframes = [...reframes];
    const fieldName = field.split('_')[0]; // 'statement', 'why', 'next_move'
    const reframe = newReframes[index];
    
    // Support both new format (wording object) and old format (direct fields)
    if (reframe.wording) {
      // New format: update wording object
      reframe.wording = { ...reframe.wording, [fieldName]: value };
    } else {
      // Old format or create wording object
      if (!reframe.wording) {
        reframe.wording = {};
      }
      reframe.wording[fieldName] = value;
      // Also update direct field for backward compatibility
      reframe[fieldName] = value;
    }
    
    const newData = { ...data, reframes: newReframes };
    if (typeof onEdit === 'function') {
      onEdit(newData);
    }
    setEditingField(null);
  };
  
  const cancelEdit = () => {
    setEditingField(null);
    setEditedValue('');
  };

  if (!reframes || reframes.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No reframes available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {userEdited && (
        <Box sx={{ mb: 2 }}>
          <Chip 
            label="✏️ User Edited" 
            size="small" 
            color="info" 
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}
      
      {/* Generation Stage Badge */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {generationStage === 'concepts' && (
          <Chip 
            label="Concepts Only - Generate Wording" 
            size="small" 
            color="warning" 
            sx={{ fontWeight: 600 }}
          />
        )}
        {generationStage === 'wording' && reframesWithoutWording > 0 && (
          <Chip 
            label={`${reframesWithoutWording} reframe(s) need wording`} 
            size="small" 
            color="warning" 
            sx={{ fontWeight: 600 }}
          />
        )}
        {!isOrientation && generationStage === 'complete' && reframesWithWording === reframes.length && (
          <Chip 
            label="Complete" 
            size="small" 
            color="success" 
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>
      
      {/* Action Buttons for Two-Step Generation */}
      {reframes.length === 0 && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={generatingConcepts ? <CircularProgress size={16} /> : <LightbulbIcon />}
            onClick={onGenerateConcepts}
            disabled={generatingConcepts || generatingWording}
            sx={{ mr: 1 }}
          >
            {generatingConcepts ? 'Generating Concepts...' : 'Generate Concepts'}
          </Button>
        </Box>
      )}
      
      {reframes.length > 0 && reframesWithoutWording > 0 && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={generatingWording ? <CircularProgress size={16} /> : <LightbulbIcon />}
            onClick={onGenerateWording}
            disabled={generatingConcepts || generatingWording}
          >
            {generatingWording ? 'Generating Wording...' : `Generate Wording (${reframesWithoutWording} remaining)`}
          </Button>
        </Box>
      )}
      
      {/* Confidence Meter - Hidden for orientation nodes */}
      {!isOrientation && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
            AI Confidence: {Math.round(confidence * 100)}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={confidence * 100} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              bgcolor: '#e2e8f0',
              '& .MuiLinearProgress-bar': { bgcolor: confidence > 0.7 ? '#10b981' : confidence > 0.4 ? '#f59e0b' : '#ef4444' }
            }} 
          />
        </Box>
      )}

      {/* Dominant Themes - Hidden for orientation nodes */}
      {!isOrientation && coverage.dominant_themes && coverage.dominant_themes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#64748b' }}>
            Dominant Themes
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {coverage.dominant_themes.map((theme, i) => (
              <Chip key={i} label={theme} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca' }} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Reframes */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
        Reframes ({reframes.length})
      </Typography>
      
      {reframes.map((reframe, idx) => {
        const display = getReframeDisplay(reframe);
        const hasWordingForThis = hasWording(reframe);
        const reframeType = reframe.concept?.type || reframe.type;
        
        return (
        <Accordion key={idx} sx={{ mb: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fafafa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <LightbulbIcon sx={{ color: REFRAME_TYPE_COLORS[reframeType] || '#9ca3af', fontSize: 20 }} />
              {editingField === `statement_${idx}` ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={editedValue}
                  onChange={(e) => setEditedValue(e.target.value)}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ flex: 1, bgcolor: 'white' }}
                />
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                  {display.statement || (reframe.concept?.concept_summary || `Reframe ${idx + 1} (Concept Only)`)}
                </Typography>
              )}
              {!hasWordingForThis && editingField !== `statement_${idx}` && (
                <Chip 
                  label="Concept Only" 
                  size="small" 
                  color="warning" 
                  sx={{ mr: 1 }}
                />
              )}
              <Chip 
                label={REFRAME_TYPE_LABELS[reframeType] || reframeType} 
                size="small" 
                sx={{ 
                  bgcolor: REFRAME_TYPE_COLORS[reframeType] + '20' || '#f3f4f6',
                  color: REFRAME_TYPE_COLORS[reframeType] || '#6b7280',
                  fontWeight: 600,
                  fontSize: '0.7rem'
                }} 
              />
              {editingField === `statement_${idx}` ? (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Box
                    component="div"
                    onClick={(e) => {
                      e.stopPropagation();
                      saveEdit(idx, `statement_${idx}`, editedValue);
                    }}
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      borderRadius: '50%',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                      color: '#10b981'
                    }}
                  >
                    <SaveIcon fontSize="small" />
                  </Box>
                  <Box
                    component="div"
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelEdit();
                    }}
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      borderRadius: '50%',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                      color: '#ef4444'
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </Box>
                </Box>
              ) : (
                <>
                  <Box
                    component="div"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEdit(`statement_${idx}`, display.statement || '');
                    }}
                    sx={{ 
                      ml: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      borderRadius: '50%',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                      color: '#64748b'
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </Box>
                  {onRegenerateItem && (
                    <Tooltip title="Regenerate this reframe">
                      <Box
                        component="div"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRegenerateItem(idx);
                        }}
                        sx={{ 
                          ml: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          cursor: regeneratingIndex === idx ? 'not-allowed' : 'pointer',
                          borderRadius: '50%',
                          '&:hover': { bgcolor: regeneratingIndex === idx ? 'transparent' : 'rgba(0, 0, 0, 0.04)' },
                          opacity: regeneratingIndex === idx ? 0.5 : 1,
                          color: 'inherit'
                        }}
                      >
                        <RefreshIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {/* Statement Field - Editable in details too */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                    Statement
                  </Typography>
                  {editingField !== `statement_${idx}` && (
                    <IconButton size="small" onClick={() => startEdit(`statement_${idx}`, display.statement || '')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {editingField === `statement_${idx}` ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={editedValue}
                      onChange={(e) => setEditedValue(e.target.value)}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<SaveIcon />} onClick={() => saveEdit(idx, `statement_${idx}`, editedValue)}>Save</Button>
                      <Button size="small" startIcon={<CancelIcon />} onClick={cancelEdit}>Cancel</Button>
                    </Stack>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    {display.statement || (reframe.concept?.concept_summary || 'No statement')}
                  </Typography>
                )}
              </Box>
              {/* Why Field */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                    Why
                  </Typography>
                  {editingField !== `why_${idx}` && (
                    <IconButton size="small" onClick={() => startEdit(`why_${idx}`, display.why || '')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {editingField === `why_${idx}` ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      value={editedValue}
                      onChange={(e) => setEditedValue(e.target.value)}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<SaveIcon />} onClick={() => saveEdit(idx, `why_${idx}`, editedValue)}>Save</Button>
                      <Button size="small" startIcon={<CancelIcon />} onClick={cancelEdit}>Cancel</Button>
                    </Stack>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    {display.why || (hasWordingForThis ? 'No explanation' : 'Wording not generated yet')}
                  </Typography>
                )}
              </Box>
              
              {/* Next Move Field */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                    Next Move
                  </Typography>
                  {editingField !== `next_move_${idx}` && (
                    <IconButton size="small" onClick={() => startEdit(`next_move_${idx}`, display.next_move || '')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                  {editingField === `next_move_${idx}` ? (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        value={editedValue}
                        onChange={(e) => setEditedValue(e.target.value)}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button size="small" startIcon={<SaveIcon />} onClick={() => saveEdit(idx, `next_move_${idx}`, editedValue)}>Save</Button>
                        <Button size="small" startIcon={<CancelIcon />} onClick={cancelEdit}>Cancel</Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      {display.next_move || 'No next move'}
                    </Typography>
                  )}
                </Box>
              
              {/* Evidence Nodes */}
              {(reframe.concept?.evidence_nodes || reframe.evidence_nodes) && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block', mb: 0.5 }}>
                    Evidence Nodes
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(reframe.concept?.evidence_nodes || reframe.evidence_nodes || []).map((node, i) => (
                      <Chip 
                        key={i} 
                        label={node} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }} 
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              
              {/* Concept Summary (if no wording yet) */}
              {!hasWordingForThis && reframe.concept?.concept_summary && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block', mb: 0.5 }}>
                    Concept Summary
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', fontStyle: 'italic' }}>
                    {reframe.concept.concept_summary}
                  </Typography>
                </Box>
              )}

            </Stack>
          </AccordionDetails>
        </Accordion>
        );
      })}

      {/* Unknowns & Assumptions */}
      {(unknowns.length > 0 || assumptions.length > 0) && (
        <Box sx={{ mt: 3, p: 2, bgcolor: '#fef3c7', borderRadius: 2, border: '1px solid #fbbf24' }}>
          {unknowns.length > 0 && (
            <Box sx={{ mb: assumptions.length > 0 ? 2 : 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', mb: 1 }}>
                Unknowns
              </Typography>
              {unknowns.map((unknown, i) => (
                <Typography key={i} variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', mb: 0.5 }}>
                  • {unknown}
                </Typography>
              ))}
            </Box>
          )}
          
          {assumptions.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', mb: 1 }}>
                Assumptions
              </Typography>
              {assumptions.map((assumption, i) => (
                <Typography key={i} variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem', mb: 0.5 }}>
                  • {assumption}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

